import { useState, useMemo } from "react";
import { parseISO } from "date-fns";

/**
 * Custom hook for managing report filters (date range + sorting)
 *
 * Usage:
 * const filters = useReportFilters(reports);
 * // Use: filters.startDate, filters.endDate, filters.sortOrder
 * // Use: filters.setStartDate, filters.setEndDate, filters.setSortOrder
 * // Use: filters.clearFilters
 * // Use: filters.filteredAndSorted (computed filtered/sorted reports)
 */
export function useReportFilters(reports = []) {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [sortOrder, setSortOrder] = useState("newest");

  const filteredAndSorted = useMemo(() => {
    let filtered = [...reports];

    // Filter by date range
    if (startDate || endDate) {
      filtered = filtered.filter(r => {
        const reportDate = parseISO(r.weekStart);
        const start = startDate ? parseISO(startDate) : new Date("1900-01-01");
        const end = endDate ? parseISO(endDate) : new Date("2099-12-31");
        return reportDate >= start && reportDate <= end;
      });
    }

    // Sort by date
    filtered.sort((a, b) => {
      const dateA = parseISO(a.weekStart);
      const dateB = parseISO(b.weekStart);
      return sortOrder === "newest" ? dateB - dateA : dateA - dateB;
    });

    return filtered;
  }, [reports, startDate, endDate, sortOrder]);

  const clearFilters = () => {
    setStartDate("");
    setEndDate("");
    setSortOrder("newest");
  };

  return {
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    sortOrder,
    setSortOrder,
    clearFilters,
    filteredAndSorted
  };
}
